/**
 * The Integracje tab (Kalendarz until 26 October 2026), as provider rows (owner-approved polish,
 * 4 November 2026): a „Kalendarz" group holding Google and Apple, the „pick one" hint under it, and
 * a „Rozmowy wideo" group holding the standing call link (calendar-sync-requirements R2, R8, R10).
 * Nothing here names an address, token or identifier of the connection (R10).
 *
 * The rows are drawn once Google's section, the Apple feed and the standing link have ALL answered.
 * They are three reads, and a row that arrived on its own would push every row under it.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { instantWhen } from '~/shared/lib/instantWhen';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { FormBanner } from '~/shared/ui/FormBanner';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AppleCalendarApi } from '../lib/useAppleCalendar';
import type { GoogleCalendarApi } from '../lib/useGoogleCalendar';
import type { StandingCallLinkApi } from '../lib/useStandingCallLink';
import { pickOneCalendarNoteShown } from '../model/appleCalendar';
import { disconnectConfirmBody, type GoogleCalendarView } from '../model/googleCalendar';
import type { GoogleCalendarProblem } from '../model/googleCalendarFlow';
import { PRIMARY_PILL, SECONDARY_PILL } from './accountButtons';
import screen from './AccountScreen.module.css';
import styles from './CalendarTab.module.css';
import { AppleCalendarBlock } from './AppleCalendarBlock';
import { GoogleCalendarMark } from './CalendarBrandIcons';
import { StandingCallLinkBlock } from './StandingCallLinkBlock';

type AccountView = Extract<GoogleCalendarView, { kind: 'connected' | 'broken' }>;

const PROBLEM_KEY: Readonly<Record<GoogleCalendarProblem, string>> = {
  connectFailed: 'web:account.calendar.connectFailed',
  scopeMissing: 'web:account.calendar.scopeMissing',
  disconnectFailed: 'web:account.calendar.disconnectFailed',
};

interface CalendarTabProps {
  readonly calendar: GoogleCalendarApi;
  readonly apple: AppleCalendarApi;
  readonly standing: StandingCallLinkApi;
}

export function CalendarTab({ calendar, apple, standing }: CalendarTabProps) {
  const { t, i18n } = useTranslation();
  const { view, state, flow } = calendar;
  /* ONE gate over the three reads every row waits for. A read that FAILED is settled (an Apple or
   * link failure is said inside its own row), so only a read still on the wire holds the bars. */
  const { waiting, skeleton, reveal } = useSkeletonGate(
    state === 'pending' || !apple.settled || !standing.settled,
  );
  /** The button that opened the confirm — where focus returns when it is still there. */
  const askedFrom = useRef<HTMLElement | null>(null);
  const failed = !waiting && state === 'error';
  const busy = flow.busy !== null;
  const connecting = flow.busy === 'connect';
  const problem = flow.problem === null ? null : t(PROBLEM_KEY[flow.problem]);
  const email = view?.kind === 'connected' || view?.kind === 'broken' ? view.email : null;
  /** Every row's answer is in: Google's section (not its wait, not its error), Apple's, the link's. */
  const drawn = !waiting && !failed && view !== null && view.kind !== 'unavailable';

  /** After a disconnect „Odłącz" is gone; the connect button that replaced it takes focus. */
  const takeFocus = (node: HTMLElement | null) => {
    if (node !== null && flow.focusConnect) {
      node.focus();
      calendar.onFocusTaken();
    }
  };

  const connectButton = (label: string) => (
    <button
      ref={takeFocus}
      type="button"
      className={PRIMARY_PILL}
      aria-disabled={busy ? 'true' : undefined}
      aria-busy={connecting}
      onClick={busy ? undefined : calendar.onConnect}
    >
      {connecting ? t('web:account.calendar.connecting') : label}
    </button>
  );

  /** R10 and R8: which account, and when its calendar was last brought up to date. Broken, nothing
   * is being written, so a calendar never written says only which account it was. */
  const accountLine = (account: AccountView) => {
    const first =
      account.email === null
        ? t('web:account.calendar.connected')
        : t('web:account.calendar.connectedAs', { email: account.email });
    const second =
      account.lastSyncedAt !== null
        ? t('web:account.calendar.lastSyncedInline', {
            when: instantWhen(account.lastSyncedAt, calendar.answeredAt, i18n.language),
          })
        : account.kind === 'connected'
          ? t('web:account.calendar.syncPendingInline')
          : null;
    return second === null ? first : t('web:account.calendar.statusPair', { first, second });
  };

  const disconnectButton = (
    <button
      type="button"
      className={SECONDARY_PILL}
      aria-disabled={busy ? 'true' : undefined}
      onClick={
        busy
          ? undefined
          : (event) => {
              askedFrom.current = event.currentTarget;
              calendar.onAskDisconnect();
            }
      }
    >
      {t('web:account.calendar.disconnect')}
    </button>
  );

  return (
    <div className={screen.inner}>
      <p className={`${String(screen.ledeText)} ${String(screen.lede)}`}>{t('web:account.calendar.lede')}</p>

      {failed ? (
        <div className={`${String(styles.failure)} ${reveal}`}>
          <h2 className={screen.blockTitle}>{t('web:account.calendar.loadErrorTitle')}</h2>
          <FormBanner message={t('web:account.calendar.loadErrorBody')} />
          <button type="button" className={SECONDARY_PILL} onClick={calendar.onRetry}>
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <>
          <h2 className={screen.groupLabel}>{t('web:account.calendar.section')}</h2>
          {/* The group stands from the first frame with Google's row in it — the one row every
            * state draws — and the wait is bars inside that row. */}
          <div className={screen.group} aria-busy={!drawn} data-clarity-mask={email === null ? undefined : 'True'}>
            {!drawn && <span className={hidden.srOnly}>{t('web:account.calendar.waiting')}</span>}

            <div className={screen.groupRow}>
              <span className={styles.tile} aria-hidden="true">
                <GoogleCalendarMark className={styles.brandGlyph} />
              </span>
              <div className={screen.rowBody}>
                <b className={styles.title}>{t('web:account.calendar.googleTitle')}</b>
                {!drawn ? (
                  skeleton && <i className={`${String(skel.bar)} ${String(styles.statusBar)}`} />
                ) : view?.kind === 'connected' ? (
                  <span className={`${String(styles.status)} ${reveal}`}>{accountLine(view)}</span>
                ) : view?.kind === 'broken' ? (
                  <span className={reveal}>
                    {/* A state, not the answer to a press: drawn, not shouted as an alert. */}
                    <span className={`${String(styles.status)} ${String(styles.broken)}`}>
                      <WarningGlyph className={styles.warning} />
                      {t('web:account.calendar.broken')}
                    </span>
                    <span className={styles.status}>{accountLine(view)}</span>
                    {/* R2: reconnecting opens the consent page too, so what the grant is for is
                      * said before it, in the same words as before the first connection. */}
                    <span className={styles.status}>{t('web:account.calendar.disclosure')}</span>
                  </span>
                ) : (
                  // R2: what the grant is for is said here, before the consent page opens.
                  <span className={`${String(styles.status)} ${reveal}`}>
                    {t('web:account.calendar.disclosure')}
                  </span>
                )}
              </div>
              {drawn && (
                <div className={`${String(screen.rowActions)} ${reveal}`}>
                  {view?.kind === 'connected' ? (
                    disconnectButton
                  ) : view?.kind === 'broken' ? (
                    <>
                      {disconnectButton}
                      {connectButton(t('web:account.calendar.reconnect'))}
                    </>
                  ) : (
                    connectButton(t('web:account.calendar.connect'))
                  )}
                </div>
              )}
            </div>
            {problem !== null && (
              <div className={styles.rowBanner}>
                <FormBanner message={problem} />
              </div>
            )}

            {drawn && <AppleCalendarBlock apple={apple} className={reveal} />}
          </div>

          {/* Google OR Apple (owner, 29 October 2026): both would draw every session twice. */}
          {drawn && pickOneCalendarNoteShown(view, apple.view) && (
            <p className={`${String(screen.groupHint)} ${reveal}`}>{t('web:account.calendar.pickOne')}</p>
          )}

          {drawn && <StandingCallLinkBlock standing={standing} className={reveal} />}
        </>
      )}

      <ConfirmDialog
        prompt={
          flow.confirm
            ? {
                title: t('web:account.calendar.disconnectConfirmTitle'),
                description: t(`web:account.calendar.${disconnectConfirmBody(view)}`, {
                  email: email ?? '',
                }),
                confirmLabel: t('web:account.calendar.disconnectConfirm'),
                cancelLabel: t('common.cancel'),
                destructiveConfirm: true,
                masked: email !== null,
                busy: flow.busy === 'disconnect',
              }
            : null
        }
        onCancel={calendar.onCancelConfirm}
        onConfirm={calendar.onConfirmDisconnect}
        finalFocus={askedFrom}
      />
    </div>
  );
}
